import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ContactOriginApiService } from '../services/contact-origin-api.service';
import { ContactOriginRequest, ContactOriginResponse } from '../models/contact-origin.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { NAME_PATTERN, normalizeForComparison } from '../../../shared/utils/text-normalization.utils';

@Component({
  selector: 'app-contact-origin-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './contact-origin-form.component.html'
})
export class ContactOriginFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  originId: number | null = null;

  // Form model
  name = '';
  private originalName = '';
  code = '';

  private existingOrigins: ContactOriginResponse[] = [];

  constructor(
    private originApi: ContactOriginApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarOrigenesExistentes();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.originId = +id;
      this.cargarOrigen(this.originId);
    }
  }

  private cargarOrigenesExistentes(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    this.originApi.getOrigins(businessId).subscribe({
      next: (data) => { this.existingOrigins = data; },
      error: () => { /* validación se delega al backend si falla */ }
    });
  }

  private esDuplicado(): { duplicado: boolean; campo?: 'name' | 'code' } {
    const targetName = normalizeForComparison(this.name);
    const targetCode = normalizeForComparison(this.code);
    for (const o of this.existingOrigins) {
      if (o.id === this.originId) continue;
      if (normalizeForComparison(o.name) === targetName) return { duplicado: true, campo: 'name' };
      if (normalizeForComparison(o.code) === targetCode) return { duplicado: true, campo: 'code' };
    }
    return { duplicado: false };
  }

  private cargarOrigen(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.originApi.getOriginById(id, businessId).subscribe({
      next: (origen) => {
        this.name = origen.name;
        this.originalName = origen.name;
        this.code = origen.code;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el origen';
        this.loading = false;
      }
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Origen de Contacto' : 'Nuevo Origen de Contacto';
  }

  guardar(): void {
    const faltantes: string[] = [];
    if (!this.name.trim()) faltantes.push('Nombre');
    if (!this.code.trim()) faltantes.push('Código');

    if (faltantes.length > 0) {
      this.error = `Complete los campos requeridos: ${faltantes.join(', ')}.`;
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    if (!NAME_PATTERN.test(this.name.trim())) {
      const msg = 'El nombre solo puede contener letras, espacios, apóstrofes y guiones';
      this.error = msg;
      this.toast.warning({ title: 'Nombre inválido', message: msg });
      return;
    }

    const dup = this.esDuplicado();
    if (dup.duplicado) {
      const campo = dup.campo === 'name' ? 'nombre' : 'código';
      const valor = dup.campo === 'name' ? this.name.trim() : this.code.trim();
      const msg = `Ya existe un origen con el ${campo} "${valor}"`;
      this.error = msg;
      this.toast.warning({ title: `${campo[0].toUpperCase() + campo.slice(1)} duplicado`, message: msg });
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: ContactOriginRequest = {
      businessId,
      name: this.name.trim(),
      code: this.code.trim()
    };

    if (this.isEditing && this.originId) {
      this.originApi.updateOrigin(this.originId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Origen de contacto',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/origenes']);
        },
        error: (err) => {
          this.saving = false;
          const msg = err.error?.message || 'Error al actualizar el origen';
          this.error = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      });
    } else {
      this.originApi.createOrigin(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Origen de contacto', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/origenes']);
        },
        error: (err) => {
          this.saving = false;
          const msg = err.error?.message || 'Error al crear el origen';
          this.error = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-contactos/origenes']);
  }
}
